/**
 * Prüfschritte aus dem Prüfprompt eines KI-Regelsets lesen.
 *
 * Der Prüfprompt nummeriert die Schritte und benennt sie in aller Regel selbst
 * („1. Beleg-Relevanz", „2. Muss-Kriterien", …). Diese Überschriften sind die stabilste
 * verfügbare Quelle für die Prüfschritt-Titel im Bericht: sie stehen fest, sobald das Regelset
 * gespeichert ist, kosten keinen KI-Aufruf und schwanken nicht von Lauf zu Lauf – anders als der
 * Titel, den das Prüfmodell je Antwort mitliefert (`check_parameter`) und gelegentlich weglässt.
 *
 * Rein, ohne Angular – gemeinsam genutzt vom KI-Editor (Schritt-Interpretation) und vom
 * Prüfbericht (Ersatz-Überschriften).
 */

/** Ein im Prüfprompt erkannter Prüfschritt. */
export interface PruefpromptStep {
  /** Kurze Überschrift des Schritts, z. B. „Beleg-Relevanz" (ohne Nummer). */
  title: string;
  /** Der restliche Text des Schritts (Unterpunkte), für die KI-Interpretation. */
  details: string;
}

/**
 * Zeilenanfänge, die einen neuen Prüfschritt einleiten:
 * `1.` / `1)` / `Schritt 1:` / `Step 1:` – jeweils am Zeilenanfang, Nummer aufsteigend nicht
 * erzwungen (ein Prompt darf auch mit „2." beginnen, wenn der Nutzer so schreibt).
 */
const STEP_START = /^[ \t]*(?:(?:schritt|step)[ \t]*)?(\d{1,2})[.):][ \t]*(.*)$/i;

/**
 * Eine Überschrift ist zu lang, wenn sie offensichtlich ein ganzer Satz ist. Der Prüfprompt
 * schreibt Schritte teils als Fließtext („1. Sichte die Dokumente auf …") – dann ist die Zeile
 * kein Titel, und es bleibt bei keiner Überschrift statt einer sinnlos langen.
 */
const MAX_TITLE_WORDS = 6;

function isUsableTitle(text: string): boolean {
  if (!text) return false;
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > MAX_TITLE_WORDS) return false;
  // Satzzeichen am Ende deutet auf einen Satz, nicht auf eine Überschrift.
  return !/[.:;!?]$/.test(text);
}

/**
 * Zerlegt den Prüfprompt in seine Prüfschritte. Leeres Ergebnis, wenn der Prompt keine
 * nummerierten Schritte enthält – dann gibt es schlicht nichts zu benennen.
 */
export function parsePruefpromptSteps(prompt: string | null | undefined): PruefpromptStep[] {
  const lines = String(prompt ?? '').split(/\r?\n/);
  const steps: Array<{ heading: string; details: string[] }> = [];
  for (const line of lines) {
    const match = STEP_START.exec(line);
    if (match) {
      steps.push({heading: (match[2] ?? '').trim(), details: []});
    } else if (steps.length > 0 && line.trim()) {
      steps[steps.length - 1].details.push(line.trim());
    }
  }
  return steps.map(step => ({
    title: isUsableTitle(step.heading) ? step.heading : '',
    details: [step.heading, ...step.details].filter(Boolean).join('\n'),
  }));
}

/**
 * Nur die Titel der Prüfschritte, in Prompt-Reihenfolge. Schritte ohne brauchbare Überschrift
 * ergeben einen leeren Eintrag – die Position im Array bleibt erhalten, damit der Index dem
 * Prüfschritt entspricht.
 */
export function parsePruefpromptStepTitles(prompt: string | null | undefined): string[] {
  return parsePruefpromptSteps(prompt).map(step => step.title);
}
